import { Fragment, useEffect, useState } from 'react';
import { DialogTitle, Dialog, Button, Box, TextField, Typography } from '@mui/material/';
import { makeStyles } from '@mui/styles';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import StaticDateRangePicker from '@mui/lab/StaticDateRangePicker';
import { DateRange } from '@mui/lab/DateRangePicker';
import { endOfDay, format } from 'date-fns';

export type IsoDateRange = [StartDate: string | null, EndDate: string | null];

export interface CalendarModalV2Props {
  /**
   * Callback for if the user closes without choosing a date.
   */
  handleClose: ((event: {}, reason: 'escapeKeyDown' | 'backdropClick') => void) | undefined;

  /**
   * Bit flag to have the modal open or not.
   */
  isOpen: boolean;

  /**
   * Bit flag to know whether to return the full iso date+time or just the date portion.
   */
  isDateOnly: boolean;

  /**
   * Callback for when the calendar is submitted.
   * @param range
   * @returns
   */
  handleCalendarSubmit: (range: IsoDateRange) => void;

  /**
   * Optional initial value for the calendar
   */
  initialDateRange?: IsoDateRange;
}

const useStyles = makeStyles({
  gridContainer: {
    background: '#FFFFFF',
    borderRadius: '24px',
  },
  dialog: {
    padding: '20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  buttontext: {
    fontFamily: 'sans-serif',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: 'small',
  },
  dialogbox: {
    borderRadius: '20',
    borderStyle: 'solid',
  },
});

/**
 * Calendar selector. Returns date only or date with time portion.
 * @param props
 * @returns
 */
const CalendarModalV2 = (props: CalendarModalV2Props) => {
  const classes = useStyles();
  const defaultStartDate = props?.initialDateRange && props?.initialDateRange?.[0] ? new Date(`${props.initialDateRange[0]}T00:00:00`) : new Date();
  const defaultEndDate = props?.initialDateRange && props?.initialDateRange?.[1] ? new Date(`${props.initialDateRange[1]}T00:00:00`) : new Date();

  //this is the value in the native date picker. It's full date object.
  const [dateRangePickerValue, setDateRangePickerValue] = useState<DateRange<Date>>([defaultStartDate, defaultEndDate]);
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);

  useEffect(() => {
    if (dateRangePickerValue?.[0] && dateRangePickerValue?.[1]) setIsSubmitEnabled(true);
    else setIsSubmitEnabled(false);
  }, [dateRangePickerValue]);

  const formatAndSubmit = (nativeValue: DateRange<Date>) => {
    let isoDateRange: IsoDateRange;
    if (props.isDateOnly) {
      isoDateRange = [nativeValue?.[0] ? format(nativeValue[0], 'yyyy-MM-dd') : null, nativeValue?.[1] ? format(nativeValue[1], 'yyyy-MM-dd') : null];
    } else {
      isoDateRange = [nativeValue?.[0] ? nativeValue[0].toISOString() : null, nativeValue?.[1] ? endOfDay(nativeValue[1]).toISOString() : null];
    }
    props.handleCalendarSubmit(isoDateRange);
  };

  return (
    <Dialog onClose={props.handleClose} open={props.isOpen} maxWidth="md" className={classes.dialogbox} PaperProps={{ style: { pointerEvents: 'auto', borderRadius: '24px' } }}>
      <DialogTitle id="subTitle">Set Start and End Dates</DialogTitle>
      <div className={classes.dialog}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <StaticDateRangePicker
            displayStaticWrapperAs="desktop"
            value={dateRangePickerValue}
            onChange={(newValue) => {
              setDateRangePickerValue(newValue);
            }}
            renderInput={(startProps, endProps) => (
              <Fragment>
                <TextField {...startProps} />
                <Box sx={{ mx: 2 }}> to </Box>
                <TextField {...endProps} />
              </Fragment>
            )}
          />
        </LocalizationProvider>
      </div>
      <Box sx={{ display: 'flex', p: 2, justifyContent: 'center' }}>
        <Button disabled={!isSubmitEnabled} variant="contained" onClick={() => formatAndSubmit(dateRangePickerValue)} sx={{ borderRadius: '20px', fontSize: 'small' }} size="large">
          <Typography className={classes.buttontext} variant="caption">
            continue
          </Typography>
        </Button>
      </Box>
    </Dialog>
  );
};
export default CalendarModalV2;
